import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { Icon } from '@/deploy/components/ui/Icon';
import { Modal } from '@/deploy/components/ui/Modal';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { networkAdminService, type NodePayload, type TargetPayload } from '@/deploy/services/network.service';
import type { CloudflareAccount, ExternalTarget, ProxyNode, SshAuthType, SshTestResult } from '@/deploy/types/network';
import { useNotificationsStore } from '@/stores/notifications.store';
import { errorMessage, formatDate } from '../networkFormat';
import styles from '../Network.module.scss';

/** Charge une liste admin et expose un rechargement. */
function useList<T>(fetcher: () => Promise<T[]>) {
  const [items, setItems] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setItems(await fetcher());
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [fetcher]);
  useEffect(() => { void reload(); }, [reload]);
  return { items, error, reload };
}

/** Exécute une action en notifiant le résultat, puis recharge la liste. */
function useAction(reload: () => Promise<void>) {
  const notify = useNotificationsStore();
  const [busy, setBusy] = useState<string | null>(null);
  const run = async (key: string, fn: () => Promise<string | void>) => {
    setBusy(key);
    try {
      const message = await fn();
      if (message) notify.success(message);
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setBusy(null);
      await reload();
    }
  };
  return { busy, run };
}

function testMessage(name: string, r: SshTestResult): string {
  if (!r.ok) throw new Error(`${name} : ${r.error ?? r.output ?? 'échec'}`);
  return `${name} : connexion OK${r.output ? ` (${r.output.split('\n')[0]})` : ''}`;
}

function ConfirmDelete({ label, onConfirm, onClose }: { label: string; onConfirm: () => Promise<void>; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal open onClose={onClose} title={`Supprimer ${label} ?`} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="danger" loading={busy} onClick={async () => { setBusy(true); await onConfirm(); onClose(); }}>Supprimer</Button>
      </>}>
      <span>Cette suppression est définitive.</span>
    </Modal>
  );
}

// ============================================================
// Comptes Cloudflare
// ============================================================

export function CloudflareCard({ onChanged }: { onChanged: () => void }) {
  const { items, error, reload } = useList(networkAdminService.accounts);
  const { busy, run } = useAction(async () => { await reload(); onChanged(); });
  const [edit, setEdit] = useState<CloudflareAccount | 'new' | null>(null);
  const [remove, setRemove] = useState<CloudflareAccount | null>(null);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h3><Icon name="external" size={16} /> Comptes Cloudflare</h3>
        <span className={styles.spacer} />
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={() => setEdit('new')}>Ajouter</Button>
      </div>
      <span className={styles.hint}>Un token par compte (Zone:Read + DNS:Edit). Les zones sont resynchronisées toutes les 6 h.</span>
      {error && <div className={styles.error}>{error}</div>}
      {items?.length === 0 && <span className={styles.muted}>Aucun compte.</span>}
      {items?.map((a) => (
        <div key={a.id} className={styles.item}>
          <div className={styles.itemMain}>
            <span className={styles.primary}>{a.name}</span>
            <span className={styles.muted}>{a.zones} zone(s) · synchro {formatDate(a.lastSyncAt)}</span>
            {a.lastError && <span className={styles.muted} style={{ color: 'var(--color-danger)' }}>{a.lastError}</span>}
          </div>
          <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} loading={busy === `sync${a.id}`}
            onClick={() => run(`sync${a.id}`, async () => {
              const r = await networkAdminService.syncAccount(a.id);
              return `${a.name} : ${r.zones} zone(s)`;
            })}>Synchro</Button>
          <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={12} />} onClick={() => setEdit(a)} aria-label="Modifier" />
          <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={() => setRemove(a)} aria-label="Supprimer" />
        </div>
      ))}
      {edit && <AccountModal account={edit === 'new' ? null : edit} onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); void reload(); onChanged(); }} />}
      {remove && <ConfirmDelete label={`le compte ${remove.name}`} onClose={() => setRemove(null)}
        onConfirm={() => run('delete', async () => { await networkAdminService.deleteAccount(remove.id); return 'Compte supprimé'; })} />}
    </div>
  );
}

function AccountModal({ account, onClose, onSaved }: { account: CloudflareAccount | null; onClose: () => void; onSaved: () => void }) {
  const notify = useNotificationsStore();
  const [name, setName] = useState(account?.name ?? '');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (account) {
        await networkAdminService.updateAccount(account.id, { name, ...(token ? { apiToken: token } : {}) });
      } else {
        const r = await networkAdminService.createAccount(name, token);
        if ('warning' in r) notify.warning(`Compte créé, mais la synchro a échoué : ${r.warning}`);
        else notify.success(`${r.name} : ${r.zones} zone(s) trouvée(s)`);
      }
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={account ? 'Modifier le compte' : 'Nouveau compte Cloudflare'} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={submit} loading={busy}>Enregistrer</Button>
      </>}>
      <div className={styles.form}>
        <label className={styles.field}>
          <span>Nom</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="paladium" autoFocus />
        </label>
        <label className={styles.field}>
          <span>Token API</span>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder={account ? 'inchangé' : 'Zone:Read + DNS:Edit'} autoComplete="off" />
        </label>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}

// ============================================================
// Nodes nginx
// ============================================================

export function NodesCard() {
  const { items, error, reload } = useList(networkAdminService.nodes);
  const { busy, run } = useAction(reload);
  const [edit, setEdit] = useState<ProxyNode | 'new' | null>(null);
  const [remove, setRemove] = useState<ProxyNode | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);

  useEffect(() => {
    networkAdminService.nodesPublicKey().then((r) => setPublicKey(r.publicKey)).catch(() => setPublicKey(null));
  }, []);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h3><Icon name="server" size={16} /> Nodes reverse proxy</h3>
        <span className={styles.spacer} />
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={() => setEdit('new')}>Ajouter</Button>
      </div>
      <span className={styles.hint}>
        nginx -t est lancé sur le node canary avant chaque reload. Clé à mettre dans <code>/root/.ssh/authorized_keys</code> de chaque node :
      </span>
      {publicKey && (
        <div className={styles.keyBox}>
          <code title={publicKey}>{publicKey}</code>
          <Button variant="ghost" size="sm" onClick={() => void navigator.clipboard.writeText(publicKey)}>Copier</Button>
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}
      {items?.length === 0 && <span className={styles.muted}>Aucun node : les changements ne seront ni validés ni rechargés.</span>}
      {items?.map((n) => (
        <div key={n.id} className={styles.item}>
          <div className={styles.itemMain}>
            <span className={styles.cellInline}>
              <span className={styles.primary}>{n.name}</span>
              {n.canary && <Badge tone="primary">Canary</Badge>}
              {!n.enabled && <Badge tone="muted">Désactivé</Badge>}
              {n.lastError ? <Badge tone="danger" dot>Erreur</Badge> : n.lastSeenAt ? <Badge tone="success" dot>OK</Badge> : null}
            </span>
            <span className={styles.mono}>{n.username}@{n.host}:{n.port}</span>
            <span className={styles.muted} title={n.lastError ?? undefined}>
              {n.lastError ?? (n.hostKeyFingerprint ? `Clé d'hôte ${n.hostKeyFingerprint}` : 'Clé d\'hôte non épinglée')}
            </span>
          </div>
          <Button variant="ghost" size="sm" loading={busy === `test${n.id}`}
            onClick={() => run(`test${n.id}`, async () => testMessage(n.name, await networkAdminService.testNode(n.id)))}>Tester</Button>
          {n.hostKeyFingerprint && (
            <Button variant="ghost" size="sm" title="Oublier la clé d'hôte (node réinstallé)"
              onClick={() => run(`reset${n.id}`, async () => { await networkAdminService.resetNodeHostKey(n.id); return 'Clé d\'hôte oubliée'; })}>
              Reset clé
            </Button>
          )}
          <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={12} />} onClick={() => setEdit(n)} aria-label="Modifier" />
          <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={() => setRemove(n)} aria-label="Supprimer" />
        </div>
      ))}
      {edit && <NodeModal node={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); void reload(); }} />}
      {remove && <ConfirmDelete label={`le node ${remove.name}`} onClose={() => setRemove(null)}
        onConfirm={() => run('delete', async () => { await networkAdminService.deleteNode(remove.id); return 'Node supprimé'; })} />}
    </div>
  );
}

function NodeModal({ node, onClose, onSaved }: { node: ProxyNode | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<NodePayload>({
    name: node?.name ?? '', host: node?.host ?? '', port: node?.port ?? 22, username: node?.username ?? 'root',
    enabled: node?.enabled ?? true, canary: node?.canary ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<NodePayload>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (node) await networkAdminService.updateNode(node.id, form);
      else await networkAdminService.createNode(form);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={node ? 'Modifier le node' : 'Nouveau node'} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={submit} loading={busy}>Enregistrer</Button>
      </>}>
      <div className={styles.form}>
        <div className={styles.formRow}>
          <label className={styles.field}><span>Nom</span>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="proxy-1" autoFocus /></label>
          <label className={styles.field}><span>Hôte</span>
            <input value={form.host} onChange={(e) => set({ host: e.target.value })} placeholder="10.0.0.11" /></label>
        </div>
        <div className={styles.formRow}>
          <label className={styles.field}><span>Port SSH</span>
            <input type="number" value={form.port} onChange={(e) => set({ port: Number(e.target.value) || 22 })} /></label>
          <label className={styles.field}><span>Utilisateur</span>
            <input value={form.username} onChange={(e) => set({ username: e.target.value })} /></label>
        </div>
        <Toggle checked={!!form.enabled} onChange={(v) => set({ enabled: v })} label="Actif" description="Rechargé à chaque changement." />
        <Toggle checked={!!form.canary} onChange={(v) => set({ canary: v })} label="Canary" description="Node utilisé pour nginx -t avant les reloads." />
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}

// ============================================================
// Serveurs externes
// ============================================================

export function TargetsCard() {
  const { items, error, reload } = useList(networkAdminService.targets);
  const { busy, run } = useAction(reload);
  const [edit, setEdit] = useState<ExternalTarget | 'new' | null>(null);
  const [remove, setRemove] = useState<ExternalTarget | null>(null);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h3><Icon name="deploy" size={16} /> Serveurs externes</h3>
        <span className={styles.spacer} />
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={() => setEdit('new')}>Ajouter</Button>
      </div>
      <span className={styles.hint}>Cibles SSH des certificats externes, utilisables par tous les projets. Les secrets ne sont jamais relus.</span>
      {error && <div className={styles.error}>{error}</div>}
      {items?.length === 0 && <span className={styles.muted}>Aucun serveur.</span>}
      {items?.map((t) => (
        <div key={t.id} className={styles.item}>
          <div className={styles.itemMain}>
            <span className={styles.cellInline}>
              <span className={styles.primary}>{t.name}</span>
              <Badge tone="muted">{t.authType === 'PASSWORD' ? 'Mot de passe' : 'Clé'}</Badge>
              {t.steps > 0 && <Badge tone="info">{t.steps} étape(s)</Badge>}
              {t.lastError && <Badge tone="danger" dot>Erreur</Badge>}
            </span>
            <span className={styles.mono}>{t.username}@{t.host}:{t.port}</span>
            <span className={styles.muted} title={t.lastError ?? undefined}>
              {t.lastError ?? (t.hostKeyFingerprint ? `Clé d'hôte ${t.hostKeyFingerprint}` : 'Clé d\'hôte non épinglée')}
            </span>
          </div>
          <Button variant="ghost" size="sm" loading={busy === `test${t.id}`}
            onClick={() => run(`test${t.id}`, async () => testMessage(t.name, await networkAdminService.testTarget(t.id)))}>Tester</Button>
          {t.hostKeyFingerprint && (
            <Button variant="ghost" size="sm" title="Oublier la clé d'hôte (serveur réinstallé)"
              onClick={() => run(`reset${t.id}`, async () => { await networkAdminService.resetTargetHostKey(t.id); return 'Clé d\'hôte oubliée'; })}>
              Reset clé
            </Button>
          )}
          <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={12} />} onClick={() => setEdit(t)} aria-label="Modifier" />
          <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={() => setRemove(t)} aria-label="Supprimer"
            disabled={t.steps > 0} title={t.steps > 0 ? 'Utilisé par une procédure de déploiement' : undefined} />
        </div>
      ))}
      {edit && <TargetModal target={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); void reload(); }} />}
      {remove && <ConfirmDelete label={`le serveur ${remove.name}`} onClose={() => setRemove(null)}
        onConfirm={() => run('delete', async () => { await networkAdminService.deleteTarget(remove.id); return 'Serveur supprimé'; })} />}
    </div>
  );
}

function TargetModal({ target, onClose, onSaved }: { target: ExternalTarget | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<TargetPayload>({
    name: target?.name ?? '', host: target?.host ?? '', port: target?.port ?? 22, username: target?.username ?? 'root',
    authType: target?.authType ?? 'PRIVATE_KEY', secret: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<TargetPayload>) => setForm((f) => ({ ...f, ...patch }));
  const authChanged = !!target && form.authType !== target.authType;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = { ...form, secret: form.secret?.trim() ? form.secret : undefined };
      if (target) await networkAdminService.updateTarget(target.id, payload);
      else await networkAdminService.createTarget(payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={target ? 'Modifier le serveur' : 'Nouveau serveur externe'} width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={submit} loading={busy}>Enregistrer</Button>
      </>}>
      <div className={styles.form}>
        <div className={styles.formRow}>
          <label className={styles.field}><span>Nom</span>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="mail-1" autoFocus /></label>
          <label className={styles.field}><span>Hôte</span>
            <input value={form.host} onChange={(e) => set({ host: e.target.value })} placeholder="203.0.113.10" /></label>
        </div>
        <div className={styles.formRow}>
          <label className={styles.field}><span>Port SSH</span>
            <input type="number" value={form.port} onChange={(e) => set({ port: Number(e.target.value) || 22 })} /></label>
          <label className={styles.field}><span>Utilisateur</span>
            <input value={form.username} onChange={(e) => set({ username: e.target.value })} /></label>
        </div>
        <label className={styles.field}>
          <span>Authentification</span>
          <select value={form.authType} onChange={(e) => set({ authType: e.target.value as SshAuthType })}>
            <option value="PRIVATE_KEY">Clé privée</option>
            <option value="PASSWORD">Mot de passe</option>
          </select>
        </label>
        <label className={styles.field}>
          <span>{form.authType === 'PASSWORD' ? 'Mot de passe' : 'Clé privée (PEM ou OpenSSH)'}</span>
          {form.authType === 'PASSWORD'
            ? <input type="password" value={form.secret} onChange={(e) => set({ secret: e.target.value })} autoComplete="off"
                placeholder={target && !authChanged ? 'inchangé' : ''} />
            : <textarea value={form.secret} onChange={(e) => set({ secret: e.target.value })} rows={5}
                placeholder={target && !authChanged ? 'inchangée' : '-----BEGIN OPENSSH PRIVATE KEY-----'} />}
        </label>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}
